import { Command, Option } from "clipanion";
import { DefaultAzureCredential, ManagedIdentityCredential, type TokenCredential } from "@azure/identity";
import { DEFAULT_POLICY_MODE, type PolicyMode } from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { buildPolicy, writePolicy, readPolicy } from "../../lib/platform-policy.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";

export class PlatformPolicyCommand extends M8tCommand {
  static paths = [["platform", "policy"]];
  static usage = Command.Usage({
    description: "Show or set how this install handles available platform updates.",
    details:
      "Modes: 'notify-only' shows updates but never applies them; 'auto-critical' (the default when unset) applies critical releases without asking; 'auto-all' applies every release. Use 'notify-only' where something else already controls what is deployed.",
    examples: [["Show the current mode", "m8t platform policy"], ["Never apply automatically", "m8t platform policy --set notify-only"]],
  });

  set = Option.String("--set", { description: "notify-only | auto-critical | auto-all" });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  miClientId = Option.String("--mi-client-id");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_POLICY_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };
    const onProgress = (m: string) => { this.context.stderr.write(`${m}\n`); };
    const credential: TokenCredential =
      typeof this.miClientId === "string" && this.miClientId.trim().length > 0
        ? new ManagedIdentityCredential({ clientId: this.miClientId.trim() })
        : new DefaultAzureCredential();
    const ctx = {
      credential,
      subscriptionId: need(this.subscription, "--subscription"),
      resourceGroup: need(this.resourceGroup, "--resource-group"),
    };

    if (typeof this.set === "string" && this.set.length > 0) {
      const policy = buildPolicy(this.set as PolicyMode, new Date().toISOString(), "cli");
      await writePolicy({ ...ctx, policy, onProgress });
      const result = { mode: policy.mode, updatedAt: policy.updatedAt };
      this.context.stdout.write(mode === "json" ? renderJson(result) + "\n" : `update policy: ${policy.mode}\n`);
      return 0;
    }

    const current = await readPolicy(ctx);
    if (current.source === "unreadable") {
      const result = { outcome: "unreadable" as const };
      this.context.stdout.write(
        mode === "json"
          ? renderJson(result) + "\n"
          : `update policy: could not be read — the current setting is unknown; do not assume it is on the default (${DEFAULT_POLICY_MODE}).\n`,
      );
      return 0;
    }
    const result =
      current.source === "explicit"
        ? { mode: current.policy.mode, outcome: "explicit" as const }
        : { mode: DEFAULT_POLICY_MODE, outcome: "absent" as const };
    this.context.stdout.write(
      mode === "json"
        ? renderJson(result) + "\n"
        : `update policy: ${result.mode}${result.outcome === "absent" ? " (default — never set)" : ""}\n`,
    );
    return 0;
  }
}
